import type { ReactNode } from 'react';
import type { FlowCellVariant, FlowLayoutTable, FlowListLayoutSpec } from '@shared/flow/flow-layouts';
import { columnStackHeader } from '@shared/flow/column-stacks';
import { GS_LEDGER_ROW_ESTIMATE_PX, LEDGER_ROW_ESTIMATE_PX, ROOMY_LEDGER_ROW_ESTIMATE_PX } from '../shell-next/table/ledgerStyles';
import type { FlowColumnDef } from '../types';

/**
 * First-paint row-height estimate per layout density. The registry stores no
 * pixel values (plan KTD 9); `rowEstimateFor` maps a table's density to one.
 */
const ROW_ESTIMATE_BY_DENSITY = {
  dense: LEDGER_ROW_ESTIMATE_PX,
  tall: GS_LEDGER_ROW_ESTIMATE_PX,
  roomy: ROOMY_LEDGER_ROW_ESTIMATE_PX,
} as const satisfies Record<NonNullable<FlowListLayoutSpec['rowDensity'][FlowLayoutTable]>, number>;

/** The first-paint row-height estimate for `table` under `spec`; absent density is dense. */
export function rowEstimateFor(spec: FlowListLayoutSpec, table: FlowLayoutTable): number {
  return ROW_ESTIMATE_BY_DENSITY[spec.rowDensity[table] ?? 'dense'];
}

/**
 * Per-cell vertical padding a density asks for, over Glaze's dense `py-2`
 * table default. Only `roomy` overrides it (`py-3`, 12px); every
 * other density keeps the table default, so those layouts render as before.
 */
const ROW_CELL_CLASS_BY_DENSITY = {
  dense: undefined,
  tall: undefined,
  roomy: 'py-3',
} as const satisfies Record<NonNullable<FlowListLayoutSpec['rowDensity'][FlowLayoutTable]>, string | undefined>;

/** The cell-padding class for `table` under `spec`, or undefined to keep the table default. */
export function rowCellClassFor(spec: FlowListLayoutSpec, table: FlowLayoutTable): string | undefined {
  return ROW_CELL_CLASS_BY_DENSITY[spec.rowDensity[table] ?? 'dense'];
}

/**
 * Skin `columns` with a list layout for one table, in one pass: relabel from
 * `spec.columnLabels[table]`, swap a column's renderer to the variant
 * `spec.cells[table][key]` names (looked up in the caller's `cells` map; a
 * variant the caller does not supply is ignored), and align from
 * `spec.columnAlign[table][key]`, else centre every column but the first when
 * the layout says `alignAllButFirst`; and stamp `headerText` /
 * `headerFilterLabels` for a column the layout's `columnStacks` declares.
 *
 * A layout is presentational only — it never adds, drops or reorders columns.
 * Returns the SAME array reference when nothing applies, so memoised rows keep
 * a stable `columns` prop.
 */
export function applyFlowListLayout<Row>(
  columns: FlowColumnDef<Row>[],
  spec: FlowListLayoutSpec,
  { table, cells }: { table: FlowLayoutTable; cells?: Partial<Record<FlowCellVariant, (row: Row) => ReactNode>> },
): FlowColumnDef<Row>[] {
  const labels = spec.columnLabels[table];
  const variants = spec.cells[table];
  const aligns = spec.columnAlign[table];
  let changed = false;
  const skinned = columns.map((column, index) => {
    const label = labels?.[column.key];
    const variant = variants?.[column.key];
    const render = variant === undefined ? undefined : cells?.[variant];
    const align = aligns?.[column.key] ?? (spec.alignAllButFirst === 'center' && index > 0 ? 'center' : undefined);
    const stack = columnStackHeader(spec, table, column.key, label ?? column.label);
    if (label === undefined && render === undefined && align === undefined && stack === undefined) return column;
    changed = true;
    return {
      ...column,
      ...(label === undefined ? {} : { label }),
      ...(render === undefined ? {} : { render }),
      ...(align === undefined ? {} : { align }),
      ...(stack === undefined ? {} : { headerText: stack.text, headerFilterLabels: stack.filterLabels }),
    };
  });
  // Nothing applied for this table: hand back the caller's own array so
  // downstream memoisation (LedgerRow) keeps its stable reference.
  return changed ? skinned : columns;
}
